import { useEffect, useRef, useState } from 'react';
import { IoBusiness } from 'react-icons/io5';
import { MdOutlineLogout } from 'react-icons/md';

import { Button } from '@hubmaster/ui/Button';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ArrowDown, RotateCw } from 'lucide-react';

import { Cart } from './components/Cart';
import { Logo } from './components/Logo';
import { Main } from './components/Main';
import { NoLogged } from './components/NoLogged';
import { WebAppInstallButton } from './components/WebAppInstall';
import { ProductRotator } from './components/minha-oferta/ProductRotator';
import { Toaster } from './components/ui/sonner';
import { useTheme } from './context/ThemeContext';
import { useApp } from './context/app';
import { useSocketIO } from './context/socket';
import { formatCNPJ } from './functions';
import { useCartMode } from './hooks/useCartMode';
import { offersTvMoveUrl } from './lib/offers-tv-move';

const queryClient = new QueryClient();

const ROTATION_STEPS = [0, 90, 180, 270] as const;

export function App() {
	const { statusSocket } = useSocketIO();
	const { setSettings } = useTheme();
	const { connection, signOut, auth, isTV } = useApp();
	const mode = useCartMode();

	const [rotationIndex, setRotationIndex] = useState(() => {
		const saved = localStorage.getItem('panel-rotation');
		return saved ? Number(saved) : 0;
	});
	const [showRotateBtn, setShowRotateBtn] = useState(false);
	const hideTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => {
		if (isTV && !localStorage.getItem('panel-rotation')) {
			setRotationIndex(1);
		}
	}, [isTV]);

	const rotation = ROTATION_STEPS[rotationIndex % ROTATION_STEPS.length];

	// A TV com ofertas migra para o Minha Oferta quando o token é marcado (D3 e D12).
	useEffect(() => {
		const url = offersTvMoveUrl({
			isTV,
			functions: connection.functions,
			token: auth.token,
			userAgent: navigator.userAgent,
			rotation,
			baseUrl: import.meta.env.VITE_MINHA_OFERTA_TV_URL,
		});
		if (url) window.location.replace(url);
	}, [isTV, connection.functions, auth.token, rotation]);

	useEffect(() => {
		setSettings((prev) => {
			return { ...prev, rotation };
		});
	}, [rotation, setSettings]);

	function handleRotate() {
		setRotationIndex((i) => {
			const next = i + 1;
			localStorage.setItem('panel-rotation', String(next));
			return next;
		});
	}

	function handleShowButton() {
		if (!isTV) return;
		setShowRotateBtn(true);
		clearTimeout(hideTimeout.current);
		hideTimeout.current = setTimeout(() => setShowRotateBtn(false), 4000);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<div className="h-screen w-screen" onClick={handleShowButton}>
				<main
					data-intv={isTV}
					className="fixed left-0 top-0 grid overflow-hidden transition-transform duration-500"
					style={{
						width: rotation === 90 || rotation === 270 ? '100vh' : '100vw',
						height: rotation === 90 || rotation === 270 ? '100vw' : '100vh',
						...(rotation !== 0
							? {
									transformOrigin: 'top left',
									transform:
										rotation === 90
											? 'rotate(90deg) translateY(-100%)'
											: rotation === 180
												? 'rotate(180deg) translate(-100%, -100%)'
												: 'rotate(270deg) translateX(-100%)',
								}
							: {}),
					}}
				>
					<div className="grid h-full w-full grid-rows-[auto_1fr_auto] items-center bg-background">
						<div>
							<header className="flex w-full items-center p-4">
								<span
									data-intv={isTV}
									className="flex w-full items-center gap-1 data-[intv=true]:mt-10 data-[intv=true]:scale-[180%] data-[intv=true]:justify-center"
								>
									<Logo size={isTV ? 50 : 30} className="text-primary" />
									<span
										data-intv={isTV}
										className="group flex flex-col justify-center"
									>
										<h1 className="text-lg font-bold leading-4 text-primary group-data-[intv=true]:text-4xl">
											{mode.cartTitle}
										</h1>
										<p className="pl-1 text-[0.75rem] text-zinc-600 group-data-[intv=true]:text-[0.55rem] group-data-[intv=true]:leading-[0.3rem]">
											Powered by <strong>HubMaster</strong>
										</p>
									</span>
								</span>

								<div className="flex flex-1 justify-end gap-1">
									<WebAppInstallButton />
									{connection.type === 'multiple' && (
										<Button
											variant="outline"
											className="p-2 text-primary"
											size={'icon'}
											onClick={() => {
												const confirm = window.confirm(
													'Tem certeza que deseja desconectar? Isso irá encerrar a sessão atual e exigir um novo login.',
												);
												if (!confirm) return;
												signOut();
											}}
										>
											<MdOutlineLogout size={24} />
										</Button>
									)}
								</div>
							</header>
							{isTV && connection.functions?.includes('offers') && (
								<div
									className="relative mt-10 w-full overflow-hidden"
									style={{ paddingTop: '56.25%' }}
								>
									<div className="absolute left-0 top-0 h-full w-full">
										<ProductRotator hideVideos />
									</div>
								</div>
							)}
						</div>
						{auth.token || statusSocket === 'connecting' ? (
							<Main />
						) : (
							<NoLogged />
						)}
						{isTV && (
							<div className="flex flex-col items-center justify-center gap-20">
								<h3 className="mt-10 text-3xl font-bold">PASSE NO LEITOR</h3>
								<ArrowDown
									size={175}
									className="animate-bounce rounded-full bg-primary text-white"
								/>
							</div>
						)}
						<footer className="pb-4">
							{auth.token && (
								<section className="flex items-center gap-1 pl-4 font-semibold">
									<IoBusiness size={24} className="text-primary" />
									<div className="flex flex-col border-l border-zinc-600 pl-1">
										<p className="text-sm font-bold leading-5 text-zinc-900">
											{connection.business?.fantasia}
										</p>
										<p className="text-[0.75rem] leading-3 text-zinc-600">
											{formatCNPJ(connection.business?.cnpj ?? '')}
										</p>
									</div>
								</section>
							)}
						</footer>
						{(connection.functions?.includes('cart') ||
							connection.functions?.includes('purchase_order')) && <Cart />}
					</div>
				</main>

				{showRotateBtn && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleRotate();
						}}
						className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition-opacity hover:bg-black/80"
						title="Girar painel"
					>
						<RotateCw size={28} />
					</button>
				)}
			</div>
			<Toaster />
		</QueryClientProvider>
	);
}
